<template>
  <div class="page-background" v-if="showPage">
    <b-container class="index">
      <!-- ヘッダー部分（共通） -->
      <div class="header">
        <div class="header-inner">
          <!-- メインロゴ（思い出アルバム） -->
          <div class="logo-container">
            <img 
              :src="localizedLogoPath"
              alt="思い出アルバム" 
              class="main-logo"
            />
          </div>
          <!-- サブロゴ（20M&7th） -->
          <div class="sub-logo">
            <img 
              src="/img/campaign/reality_histories_20m/img_logo_jp.png"
              alt="20M&7th Logo" 
              class="sub-logo-img"
            />
          </div>
        </div>
      </div>
      
      <!-- すべての年で活動がない場合の表示 -->
      <AllYearsEmptyState v-if="isAllYearsEmpty" />
      
      <!-- 通常のコンテンツ -->
      <template v-else>
        <Top 
          :user="historyData ? historyData.user : null"
          :highest-score="historyData ? historyData.highestTotalScore : 0"
          :highest-score-year="historyData ? historyData.highestTotalScoreKey : ''"
        />
        <Tab 
          :available-years="availableYears"
          @year-changed="onYearChanged" 
        />
        <TabContent 
          :selected-year="selectedYear"
          :year-data="currentYearData"
          :user="historyData ? historyData.user : null"
        />
      </template>
    </b-container>
  </div>
  <not-found class="viewer__notfound" v-else></not-found>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-property-decorator'
import Top from '@/components/Campaign/RealityHistories20M/Top.vue'
import Tab from '@/components/Campaign/RealityHistories20M/Tab.vue'
import TabContent from '@/components/Campaign/RealityHistories20M/TabContent.vue'
import AllYearsEmptyState from '@/components/Campaign/RealityHistories20M/AllYearsEmptyState.vue'
import NotFound from '@/components/Error/NotFound.vue'
import { HistoryAlbumGetResponse } from '@/api/pb/HistoryAlbum/HistoryAlbumGet'
import { HistoryAlbumKeyedData } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumKeyedData'
import { getToken } from '@/utils/token'
import { getLanguage, setLanguage, resetLanguage, LANG_JA, LANG_EN } from '@/i18n'
import { redirectToTrackerURLIfNeeded } from '@/utils/adjust_redirector'
import { HistoryAlbumGetRequest } from '@/api/pb/HistoryAlbum/HistoryAlbumGet'
import { apiV2 } from '@/api/appGatewayServerV2Api'

@Component({
  components: {
    Top,
    Tab,
    TabContent,
    AllYearsEmptyState,
    NotFound
  }
})
export default class HistoryAlbum20M extends Vue {
  // 選択された年
  selectedYear: number = 2024

  // APIレスポンスデータ
  historyData: HistoryAlbumGetResponse | null = null

  // 年別データのマップ（keyフィールドでインデックス化）
  yearDataMap: Map<string, HistoryAlbumKeyedData> = new Map()

  // 表示期間の設定
  showEndDate: Date = new Date('2025/07/31 23:59 +0900')

  // 画面の最小幅
  screenMinWidth = 375

  // 利用可能な年のリスト(2019から2024まで)
  availableYears: number[] = [2019, 2020, 2021, 2022, 2023, 2024]

  // log localize
  get localizedLogoPath(): string {
    return this.isJapanese ? '/img/campaign/reality_histories_20m/logo.png' : '/img/campaign/reality_histories_20m/logo_en.png'
  }

  // すべての年で活動がないかどうか
  get isAllYearsEmpty(): boolean {
    // データがロードされていない場合は活動無しと同じ表示をする
    if (!this.historyData) {
      return true
    }
    
    // keyedDataが存在しないか、長さが0の場合か、全ての年でmyRecordが存在しないか、myRecord.totalBroadcastViewHourが0時間は活動がない
    return !this.historyData.keyedData || this.historyData.keyedData.length === 0 || !this.historyData.keyedData.some(data => data.myRecord && data.myRecord.totalBroadcastViewHour > 0)
  }

  // 現在選択されている年のデータを取得
  get currentYearData(): HistoryAlbumKeyedData | null {
    if (!this.historyData || !this.historyData.keyedData) {
      return null
    }
    
    return this.yearDataMap.get(this.selectedYear.toString()) || null
  }

  // ページ表示可能かどうか
  get showPage(): boolean {
    const time = new Date()
    return time.getTime() <= this.showEndDate.getTime()
  }

  // 日本語かどうか
  get isJapanese(): boolean {
    return getLanguage() === LANG_JA
  }

  // created ライフサイクル
  created() {
    redirectToTrackerURLIfNeeded()
  }

  // beforeMount ライフサイクル
  async beforeMount() {
    // 端末サイズの横幅が小さすぎる場合はリサイズ
    this.resize()

    // 思い出アルバムは12言語対応するとレイアウトが色々と崩れるので仕様として日英のみ対応
    // 日本語以外だったら英語にする強引な対応で (beforeUnmountで元に戻す)
    const lang = getLanguage()
    if (lang !== LANG_JA) {
      setLanguage(LANG_EN)
    }

    // アプリ外で開いてtokenがないときはダウンロードページ
    if (getToken() === undefined) {
      this.moveDownload()
      return
    }

    // データの取得
    await this.fetchHistoryData()
  }

  // beforeDestroy ライフサイクル
  beforeDestroy() {
    // 言語設定を元に戻す
    resetLanguage()
  }

  // 画面リサイズ処理
  resize() {
    if (screen.width < this.screenMinWidth) {
      const scale = screen.width / this.screenMinWidth
      document.body.style.transform = 'scale(' + scale + ')'
      document.body.style.transformOrigin = '0 0'
      document.body.style.margin = '0'
    }
  }

  // ダウンロードページに移動
  moveDownload() {
    location.href = '/download'
  }

  // 年が変更された時のハンドラー
  onYearChanged(year: number) {
    this.selectedYear = year
  }

  // 履歴データを取得する関数
  async fetchHistoryData() {
    try {
      // query parameter からvliveIdを取得
      const url = new URL(location.href);
      const vliveId = url.searchParams.get('vliveId') || '';
      const request: HistoryAlbumGetRequest = {
        vliveId,
      }

      this.historyData = await apiV2().historyAlbum.get(request)
      
      // keyedDataからmapを作成
      this.createYearDataMap()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('履歴データの取得中にエラーが発生しました:', error)
    }
  }

  // keyedDataからkeyフィールドでインデックス化したmapを作成
  createYearDataMap() {
    this.yearDataMap.clear()
    
    if (this.historyData && this.historyData.keyedData) {
      this.historyData.keyedData.forEach(data => {
        this.yearDataMap.set(data.key, data)
      })
    }
  }
}
</script>

<style scoped>
/* 画面全体の背景 */
.page-background {
  min-height: 100vh;
  width: 100%;
  min-width: 375px;
  background-image: url('/img/campaign/reality_histories_20m/bg_pattern.png');
  background-size: 100% auto;
  background-repeat: repeat-y;
  position: relative;
}

.index {
  display: flex;
  padding-top: 16px;
  padding-bottom: 80px;
  padding-left: 0;
  padding-right: 0;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  position: relative;
  font-family: system-ui;
  color: #000000;
  width: 100% !important;
  max-width: 100% !important;
}

/* ヘッダー */
.header {
  position: relative;
  width: 100%;
  flex-shrink: 0;
  background: #ffffff;
}

.header-inner {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  position: relative;
  width: 100%;
}

/* ロゴコンテナ */
.logo-container {
  height: 70px;
  position: relative;
  width: 170px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-logo {
  width: 170px;
  height: auto;
  max-height: 70px;
  object-fit: contain;
}

/* サブロゴ */
.sub-logo {
  height: 40px;
  position: relative;
  width: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sub-logo-img {
  width: 80px;
  height: 40px;
  object-fit: contain;
}
</style>
